export const industriesData = {
  healthcare: {
    id: 'healthcare',
    title: 'Healthcare',
    subtitle: 'Transforming Patient Care with Intelligent Automation',
    icon: '🏥',
    heroImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'We help healthcare providers, payers, and health-tech companies automate documentation, enhance patient experience, and unlock insights from clinical data, securely and compliantly.',
    stats: [
      { number: '40-60%', label: 'Reduction in documentation time' },
      { number: '70%', label: 'Self-service on patient queries' },
      { number: '95%', label: 'Accuracy in medical coding' },
      { number: '24/7', label: 'Patient support availability' }
    ],
    challenges: [
      {
        icon: 'bi-clock-history',
        title: 'Documentation Burden',
        description: 'Clinicians spend 2-3 hours daily on documentation, leading to burnout and reduced patient interaction time.',
        impact: 'High administrative overhead'
      },
      {
        icon: 'bi-people',
        title: 'Patient Experience',
        description: 'Long wait times and limited self-service options result in poor patient satisfaction and increased support costs.',
        impact: 'Low patient engagement'
      },
      {
        icon: 'bi-file-earmark-text',
        title: 'Unstructured Data',
        description: 'Valuable insights locked in clinical notes, imaging reports, and patient records remain untapped.',
        impact: 'Missed opportunities'
      },
      {
        icon: 'bi-diagram-3',
        title: 'System Fragmentation',
        description: 'Data scattered across EMR, CRM, claims, and patient portals creates inefficiencies and errors.',
        impact: 'Poor data integration'
      }
    ],
    solutions: [
      {
        icon: 'bi-stars',
        service: 'Clinical AI Assistant',
        description: 'AI-powered documentation that drafts clinical notes, auto-populates forms, and summarizes patient encounters.',
        features: ['Voice-to-text transcription', 'Smart templates', 'Auto-coding assistance', 'Real-time suggestions']
      },
      {
        icon: 'bi-robot',
        service: 'Patient Engagement Bot',
        description: 'Intelligent chatbots for appointment scheduling, medication reminders, and health education.',
        features: ['Multi-language support', 'HIPAA compliance', 'Integration with EMR', '24/7 availability']
      },
      {
        icon: 'bi-graph-up-arrow',
        service: 'Predictive Analytics',
        description: 'ML models for risk stratification, readmission prediction, and early disease detection.',
        features: ['Risk scoring', 'Early warning systems', 'Population health insights', 'Treatment optimization']
      },
      {
        icon: 'bi-shield-check',
        service: 'Compliance Automation',
        description: 'Automated HIPAA compliance, audit trails, and secure data handling across all systems.',
        features: ['PHI detection', 'Access controls', 'Audit logging', 'Security monitoring']
      }
    ],
    integrations: ['Epic Systems', 'Cerner', 'Athenahealth', 'Salesforce Health Cloud', 'Microsoft Dynamics 365'],
    compliance: ['HIPAA', 'HITECH', 'SOC 2 Type II', 'HITRUST', 'GDPR'],
    outcomes: [
      '40-60% reduction in clinician documentation time',
      '70% of patient queries resolved through self-service',
      '95% accuracy in medical coding and billing',
      '30% improvement in patient satisfaction scores'
    ],
    caseStudies: [
      {
        title: 'AxleFlow - Intelligent Transport Dispatch System',
        challenge: 'Axle Health faced critical operational bottlenecks with patient transport coordination. Dispatchers were overwhelmed by 500+ daily transport requests, leading to 45-minute average response times.',
        solution: 'Engineered an enterprise-grade GenAI transport assistant using advanced RAG architecture with LangChain. The system integrates with Epic EHR, hospital scheduling systems, and ambulance dispatch APIs.',
        results: ['80% automated requests', '60% faster response time', '45-minute to 15-minute response time', 'Improved patient satisfaction'],
        technologies: ['LangChain', 'RAG', 'Epic EHR Integration', 'Python', 'FastAPI'],
        duration: '3 months'
      },
      {
        title: 'Clinical Documentation Assistant',
        challenge: 'Clinicians spending 3+ hours daily on documentation, leading to burnout and reduced patient interaction time.',
        solution: 'Implemented AI-powered clinical documentation assistant with voice-to-text transcription and smart templates.',
        results: ['50% reduction in documentation time', 'Improved clinician satisfaction', 'Better patient care quality', '95% accuracy in medical coding'],
        technologies: ['Voice-to-text', 'NLP', 'Clinical AI', 'EMR Integration'],
        duration: '4 months'
      }
    ],
    trends: [
      'Telemedicine integration and remote patient monitoring',
      'AI-powered diagnostic assistance and imaging analysis',
      'Personalized medicine and precision healthcare',
      'Interoperability and FHIR standards adoption'
    ]
  },
  banking: {
    id: 'banking',
    title: 'Financial Services',
    subtitle: 'Securing Transactions, Enhancing Customer Experience',
    icon: '🏦',
    heroImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'We help financial institutions reduce fraud, accelerate onboarding, and improve servicing with AI‑powered automation and insights.',
    stats: [
      { number: '20-40%', label: 'Fraud loss reduction' },
      { number: '60%', label: 'Faster onboarding' },
      { number: '25-40%', label: 'Reduced call volume' },
      { number: '99.9%', label: 'Uptime guarantee' }
    ],
    challenges: [
      {
        icon: 'bi-shield-exclamation',
        title: 'Fraud & Security',
        description: 'Sophisticated fraud schemes cost banks billions annually, requiring real-time detection and prevention.',
        impact: 'High financial losses'
      },
      {
        icon: 'bi-person-check',
        title: 'KYC & Onboarding',
        description: 'Manual verification processes take days or weeks, leading to customer abandonment and compliance risks.',
        impact: 'Poor customer experience'
      },
      {
        icon: 'bi-headset',
        title: 'Customer Service',
        description: 'High call volumes and long wait times result in poor customer satisfaction and increased operational costs.',
        impact: 'Low customer retention'
      },
      {
        icon: 'bi-graph-down',
        title: 'Operational Efficiency',
        description: 'Manual processes across multiple systems create bottlenecks and increase error rates.',
        impact: 'High operational costs'
      }
    ],
    solutions: [
      {
        icon: 'bi-shield-check',
        service: 'Fraud Detection AI',
        description: 'Real-time fraud detection using machine learning to identify suspicious transactions and patterns.',
        features: ['Real-time monitoring', 'Behavioral analysis', 'Risk scoring', 'Automated alerts']
      },
      {
        icon: 'bi-person-badge',
        service: 'Digital Onboarding',
        description: 'Streamlined KYC/AML processes with document verification and identity authentication.',
        features: ['Document verification', 'Biometric authentication', 'Compliance checks', 'Instant approval']
      },
      {
        icon: 'bi-robot',
        service: 'Virtual Banking Assistant',
        description: 'Intelligent chatbots for account management, transaction support, and financial advice.',
        features: ['Account management', 'Transaction support', 'Financial advice', 'Multi-channel support']
      },
      {
        icon: 'bi-graph-up-arrow',
        service: 'Risk Analytics',
        description: 'Advanced analytics for credit scoring, portfolio management, and regulatory reporting.',
        features: ['Credit scoring', 'Portfolio analysis', 'Regulatory reporting', 'Stress testing']
      }
    ],
    integrations: ['Fiserv', 'FIS', 'Jack Henry', 'Salesforce Financial Services', 'Microsoft Dynamics 365'],
    compliance: ['PCI DSS', 'SOC 2', 'GLBA', 'SOX', 'GDPR'],
    outcomes: [
      '20-40% reduction in fraud losses',
      '60% faster customer onboarding',
      '25-40% reduction in call center volume',
      'Improved regulatory compliance scores'
    ],
    caseStudies: [
      {
        title: 'MandolinKYC - Smart Document Verification System',
        challenge: 'Manual identity verification processes causing delays and errors in customer onboarding.',
        solution: 'Implemented automated ID card OCR system using easyocr and Python for seamless identity verification.',
        results: ['40% faster identity verification', 'Reduced manual errors', 'Improved customer experience'],
        technologies: ['easyocr', 'Python', 'OCR', 'Image Preprocessing'],
        duration: '2 months'
      },
      {
        title: 'AI-Powered Fraud Detection System',
        challenge: 'High fraud losses and slow detection of suspicious transactions.',
        solution: 'Implemented AI-powered fraud detection system with real-time monitoring and automated alerts.',
        results: ['35% reduction in fraud losses', 'Real-time threat detection', 'Improved security posture'],
        technologies: ['Machine Learning', 'Real-time Analytics', 'Risk Scoring', 'API Integration'],
        duration: '3 months'
      },
      {
        title: 'Digital Customer Onboarding',
        challenge: 'Slow and complex customer onboarding process leading to high abandonment rates.',
        solution: 'Streamlined digital onboarding with AI-powered document processing and automated verification.',
        results: ['50% faster onboarding', 'Improved member satisfaction', 'Reduced processing time'],
        technologies: ['Document AI', 'Automated Verification', 'Digital Workflow', 'Compliance Tools'],
        duration: '2.5 months'
      }
    ],
    trends: [
      'Open banking and API integration',
      'Cryptocurrency and blockchain solutions',
      'RegTech and compliance automation',
      'Personalized financial advice and robo-advisors'
    ]
  },
  retail: {
    id: 'retail',
    title: 'Retail & E-commerce',
    subtitle: 'Personalizing Shopping, Optimizing Operations',
    icon: '🛍️',
    heroImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'Boost conversion, reduce returns, and run smarter operations with AI across the entire shopper journey.',
    stats: [
      { number: '25-40%', label: 'Conversion rate increase' },
      { number: '30%', label: 'Customer satisfaction boost' },
      { number: '20%', label: 'Inventory cost reduction' },
      { number: '50%', label: 'Support efficiency gain' }
    ],
    challenges: [
      {
        icon: 'bi-cart-x',
        title: 'Cart Abandonment',
        description: 'High cart abandonment rates due to poor user experience, unclear pricing, and lack of personalization.',
        impact: 'Lost revenue opportunities'
      },
      {
        icon: 'bi-headset',
        title: 'Customer Support',
        description: 'Peak support volumes overwhelm teams, leading to long response times and poor customer satisfaction.',
        impact: 'Poor customer experience'
      },
      {
        icon: 'bi-box-seam',
        title: 'Inventory Management',
        description: 'Poor demand forecasting leads to stockouts, overstock, and increased carrying costs.',
        impact: 'Revenue and cost impact'
      },
      {
        icon: 'bi-arrow-repeat',
        title: 'Returns & Refunds',
        description: 'High return rates due to poor product descriptions, sizing issues, and customer expectations.',
        impact: 'Increased operational costs'
      }
    ],
    solutions: [
      {
        icon: 'bi-graph-up-arrow',
        service: 'Personalization Engine',
        description: 'AI-powered product recommendations and personalized shopping experiences.',
        features: ['Product recommendations', 'Dynamic pricing', 'Personalized content', 'Behavioral targeting']
      },
      {
        icon: 'bi-robot',
        service: 'Customer Support AI',
        description: 'Intelligent chatbots for order tracking, returns processing, and product inquiries.',
        features: ['Order tracking', 'Returns processing', 'Product support', 'Multi-language support']
      },
      {
        icon: 'bi-box-seam',
        service: 'Inventory Optimization',
        description: 'ML-powered demand forecasting and inventory management across all channels.',
        features: ['Demand forecasting', 'Stock optimization', 'Supplier management', 'Real-time tracking']
      },
      {
        icon: 'bi-chat-text',
        service: 'Sentiment Analysis',
        description: 'Real-time analysis of customer feedback, reviews, and social media sentiment.',
        features: ['Review analysis', 'Social monitoring', 'Trend detection', 'Quality insights']
      }
    ],
    integrations: ['Shopify', 'WooCommerce', 'Magento', 'Salesforce Commerce Cloud', 'Oracle Commerce'],
    compliance: ['PCI DSS', 'GDPR', 'CCPA', 'SOC 2', 'ISO 27001'],
    outcomes: [
      '25-40% increase in conversion rates',
      '30% improvement in customer satisfaction',
      '20% reduction in inventory carrying costs',
      '50% faster customer support resolution'
    ],
    caseStudy: {
      title: 'Fashion E-commerce Platform',
      challenge: 'High cart abandonment and poor customer support',
      solution: 'Implemented AI personalization and support automation',
      results: ['35% increase in conversions', '40% reduction in support tickets', 'Improved customer retention']
    },
    trends: [
      'Omnichannel retail and unified commerce',
      'Voice commerce and conversational AI',
      'Augmented reality for virtual try-ons',
      'Sustainability and ethical shopping features'
    ]
  },
  transport: {
    id: 'transport',
    title: 'Transportation & Logistics',
    subtitle: 'Optimizing Routes, Predicting Maintenance',
    icon: '🚚',
    heroImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'Optimize routes, predict maintenance, and streamline supply chains with AI-powered logistics solutions.',
    stats: [
      { number: '30%', label: 'Route efficiency improvement' },
      { number: '25%', label: 'Maintenance cost reduction' },
      { number: '20%', label: 'Delivery time optimization' },
      { number: '40%', label: 'Fuel cost savings' }
    ],
    challenges: [
      {
        icon: 'bi-geo-alt',
        title: 'Route Optimization',
        description: 'Inefficient route planning leads to increased fuel costs, delivery delays, and poor customer satisfaction.',
        impact: 'High operational costs'
      },
      {
        icon: 'bi-tools',
        title: 'Maintenance Management',
        description: 'Unpredictable equipment failures cause costly downtime and disrupt delivery schedules.',
        impact: 'Unplanned downtime costs'
      },
      {
        icon: 'bi-diagram-3',
        title: 'Supply Chain Disruptions',
        description: 'Complex supply chains are vulnerable to delays, shortages, and coordination issues.',
        impact: 'Delivery delays and costs'
      },
      {
        icon: 'bi-truck',
        title: 'Fleet Management',
        description: 'Poor visibility into fleet operations leads to inefficiencies and compliance issues.',
        impact: 'Operational inefficiencies'
      }
    ],
    solutions: [
      {
        icon: 'bi-geo-alt',
        service: 'Route Optimization AI',
        description: 'Dynamic route planning considering traffic, weather, and delivery constraints.',
        features: ['Real-time routing', 'Traffic integration', 'Weather optimization', 'Multi-stop planning']
      },
      {
        icon: 'bi-tools',
        service: 'Predictive Maintenance',
        description: 'ML models to predict equipment failures and optimize maintenance schedules.',
        features: ['Failure prediction', 'Maintenance scheduling', 'Parts optimization', 'Downtime reduction']
      },
      {
        icon: 'bi-truck',
        service: 'Fleet Management',
        description: 'Real-time tracking and optimization of fleet operations and driver performance.',
        features: ['Real-time tracking', 'Driver analytics', 'Fuel optimization', 'Compliance monitoring']
      },
      {
        icon: 'bi-diagram-3',
        service: 'Supply Chain Analytics',
        description: 'End-to-end visibility and optimization of supply chain operations.',
        features: ['Demand forecasting', 'Inventory optimization', 'Supplier management', 'Risk assessment']
      }
    ],
    integrations: ['SAP Transportation', 'Oracle Transportation', 'Manhattan Associates', 'Blue Yonder', 'Descartes'],
    compliance: ['DOT regulations', 'FMCSA', 'ISO 14001', 'C-TPAT', 'GDPR'],
    outcomes: [
      '30% improvement in route efficiency',
      '25% reduction in maintenance costs',
      '20% optimization in delivery times',
      '40% reduction in fuel costs'
    ],
    caseStudies: [
      {
        title: 'SeezAuto - AI Sales Assistant for Car Leasing',
        challenge: 'Low conversion rates and high customer support costs in car leasing operations.',
        solution: 'Built conversational sales chatbot with FastAPI, Docker, and AWS EC2 for automated customer interactions and lead qualification.',
        results: ['15% sales increase', '24/7 AI chatbot with instant answers', 'Reduced support costs'],
        technologies: ['FastAPI', 'Docker', 'AWS EC2', 'LangChain', 'RAG', 'LLMs'],
        duration: '3 months'
      },
      {
        title: 'AI Route Optimization System',
        challenge: 'Inefficient routes and high maintenance costs affecting delivery performance.',
        solution: 'Implemented AI route optimization and predictive maintenance system for fleet management.',
        results: ['35% improvement in efficiency', '30% reduction in costs', 'Improved customer satisfaction'],
        technologies: ['Route Optimization', 'Predictive Analytics', 'Fleet Management', 'IoT Sensors'],
        duration: '4 months'
      }
    ],
    trends: [
      'Autonomous vehicles and drone delivery',
      'IoT sensors and real-time monitoring',
      'Green logistics and sustainability',
      'Last-mile delivery optimization'
    ]
  },
  cybersecurity: {
    id: 'cybersecurity',
    title: 'Cybersecurity',
    subtitle: 'Protecting Digital Assets with Intelligent Security',
    icon: '🛡️',
    heroImage: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'Advanced AI-powered cybersecurity solutions to detect, prevent, and respond to threats in real-time.',
    stats: [
      { number: '99.9%', label: 'Threat detection accuracy' },
      { number: '60%', label: 'Faster incident response' },
      { number: '45%', label: 'Reduced false positives' },
      { number: '24/7', label: 'Continuous monitoring' }
    ],
    challenges: [
      {
        icon: 'bi-shield-exclamation',
        title: 'Advanced Persistent Threats',
        description: 'Sophisticated cyber attacks that evade traditional security measures and remain undetected for months.',
        impact: 'Data breaches and financial losses'
      },
      {
        icon: 'bi-clock',
        title: 'Slow Incident Response',
        description: 'Manual threat detection and response processes take hours or days, allowing attacks to spread.',
        impact: 'Extended downtime and damage'
      },
      {
        icon: 'bi-exclamation-triangle',
        title: 'False Positives',
        description: 'High volume of false alerts overwhelms security teams and reduces response effectiveness.',
        impact: 'Alert fatigue and missed threats'
      },
      {
        icon: 'bi-people',
        title: 'Skills Shortage',
        description: 'Lack of qualified cybersecurity professionals to handle increasing threat complexity.',
        impact: 'Inadequate security coverage'
      }
    ],
    solutions: [
      {
        icon: 'bi-shield-check',
        service: 'AI-Powered Threat Detection',
        description: 'Machine learning models that identify and classify threats in real-time with high accuracy.',
        features: ['Behavioral analysis', 'Anomaly detection', 'Threat intelligence', 'Real-time alerts']
      },
      {
        icon: 'bi-lightning',
        service: 'Automated Incident Response',
        description: 'Intelligent automation that responds to threats instantly, reducing response time from hours to seconds.',
        features: ['Automated containment', 'Threat isolation', 'Response orchestration', 'Recovery automation']
      },
      {
        icon: 'bi-graph-up-arrow',
        service: 'Security Analytics',
        description: 'Advanced analytics to identify patterns, predict attacks, and optimize security posture.',
        features: ['Predictive analytics', 'Risk scoring', 'Vulnerability assessment', 'Compliance monitoring']
      },
      {
        icon: 'bi-robot',
        service: 'Security Operations Center (SOC)',
        description: 'AI-enhanced SOC with intelligent monitoring, investigation, and threat hunting capabilities.',
        features: ['24/7 monitoring', 'Threat hunting', 'Incident investigation', 'Security reporting']
      }
    ],
    integrations: ['SIEM Systems', 'EDR/XDR Platforms', 'Firewall Management', 'Identity Management', 'Cloud Security'],
    compliance: ['SOC 2', 'ISO 27001', 'NIST Framework', 'GDPR', 'HIPAA'],
    outcomes: [
      '99.9% threat detection accuracy',
      '60% faster incident response times',
      '45% reduction in false positives',
      'Improved compliance and audit readiness'
    ],
    caseStudies: [
      {
        title: 'SMSGuard - Mobile Security Protection Suite',
        challenge: 'High volume of SMS threats and phishing attempts requiring manual classification.',
        solution: 'Implemented BERT-based SMS threat classification system for automated detection and response.',
        results: ['98% classification accuracy', '40% decrease in cyber attacks', 'Automated threat detection'],
        technologies: ['BERT', 'Python', 'NLP', 'Machine Learning'],
        duration: '2 months'
      },
      {
        title: 'SecureMail Pro - Advanced Email Security Suite',
        challenge: 'High volume of malicious emails and phishing attempts overwhelming security teams.',
        solution: 'Built email threat classification system using TF-IDF, Word2Vec, and Random Forest for automated detection.',
        results: ['95% classification accuracy', '40% reduction in malicious emails', 'Faster incident response'],
        technologies: ['TF-IDF', 'Word2Vec', 'Random Forest', 'Anomaly Detection'],
        duration: '2.5 months'
      },
      {
        title: 'AI-Powered Security Operations Center',
        challenge: 'High volume of security alerts and slow incident response times.',
        solution: 'Implemented AI-powered SOC with intelligent monitoring, investigation, and threat hunting capabilities.',
        results: ['90% reduction in response time', '50% fewer false positives', 'Improved security posture'],
        technologies: ['Threat Intelligence', 'SIEM Integration', 'Automated Response', 'Security Analytics'],
        duration: '3 months'
      }
    ],
    trends: [
      'Zero Trust architecture and identity-centric security',
      'Cloud-native security and DevSecOps',
      'Threat intelligence sharing and collaboration',
      'Quantum-resistant cryptography and post-quantum security'
    ]
  },
  education: {
    id: 'education',
    title: 'Education',
    subtitle: 'Empowering Learning with Intelligent Automation',
    icon: '🎓',
    heroImage: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    overview: 'We help educational institutions, EdTech companies, and training organizations automate administrative tasks, enhance student engagement, and personalize learning experiences with AI-powered solutions.',
    stats: [
      { number: '5+ hours', label: 'Daily time saved per teacher' },
      { number: '70%', label: 'Increase in student engagement' },
      { number: '60%', label: 'Faster assignment processing' },
      { number: '95%', label: 'Accuracy in grading assistance' }
    ],
    challenges: [
      {
        icon: 'bi-clock-history',
        title: 'Administrative Overload',
        description: 'Teachers spend 5+ hours daily on administrative tasks like assignment tracking, grading, and student communication.',
        impact: 'Reduced teaching time'
      },
      {
        icon: 'bi-people',
        title: 'Student Engagement',
        description: 'Traditional learning methods struggle to maintain student interest and provide personalized learning experiences.',
        impact: 'Lower learning outcomes'
      },
      {
        icon: 'bi-file-earmark-text',
        title: 'Assignment Management',
        description: 'Manual tracking of submissions, chasing non-submitters, and maintaining spreadsheets creates inefficiencies.',
        impact: 'Time-consuming processes'
      },
      {
        icon: 'bi-graph-up',
        title: 'Learning Analytics',
        description: 'Limited insights into student progress and learning patterns hinder personalized education delivery.',
        impact: 'Generic learning approaches'
      }
    ],
    solutions: [
      {
        icon: 'bi-robot',
        service: 'Teacher Operations Copilot',
        description: 'AI-powered assistant that automates assignment tracking, student communication, and administrative tasks.',
        features: ['Automated follow-ups', 'Submission tracking', 'Compliance trails', 'Email automation']
      },
      {
        icon: 'bi-mortarboard',
        service: 'Learning Assistant',
        description: 'Intelligent tutoring system that provides personalized learning experiences and real-time feedback.',
        features: ['Adaptive learning', 'Progress tracking', 'Interactive content', 'Performance analytics']
      },
      {
        icon: 'bi-chat-dots',
        service: 'Student Support Bot',
        description: '24/7 AI assistant that answers student questions, provides guidance, and supports learning.',
        features: ['Instant responses', 'Course guidance', 'Resource recommendations', 'Progress monitoring']
      },
      {
        icon: 'bi-graph-up-arrow',
        service: 'Learning Analytics',
        description: 'Advanced analytics to track student progress, identify learning gaps, and optimize curriculum.',
        features: ['Performance dashboards', 'Predictive analytics', 'Learning path optimization', 'Outcome tracking']
      }
    ],
    integrations: ['LMS Platforms', 'Student Information Systems', 'Assessment Tools', 'Communication Platforms', 'Learning Management Systems'],
    compliance: ['FERPA', 'COPPA', 'GDPR', 'Accessibility Standards', 'Data Privacy Regulations'],
    outcomes: [
      '5+ hours saved per teacher daily',
      '70% increase in student engagement',
      '60% faster assignment processing',
      '95% accuracy in automated grading'
    ],
    caseStudies: [
      {
        title: 'SullyEdu - Smart Teacher Assistant Platform',
        challenge: 'Teachers spent several hours daily reconciling submissions, chasing non‑submitters, and maintaining spreadsheets.',
        solution: 'Built an agentic RAG assistant with LangChain and LangGraph that queries submission systems and automatically emails students who have not submitted, with an auditable trail.',
        results: ['5 hours/day saved per teacher', 'Automated follow‑ups with compliance trail', 'Higher on‑time submission rates'],
        technologies: ['LangChain', 'LangGraph', 'Agentic AI', 'Email Automation'],
        duration: '2 months'
      },
      {
        title: 'InterviewPro - AI-Powered Career Coach',
        challenge: 'Candidates needed real‑time coaching, note‑taking, and feedback to improve interview outcomes.',
        solution: 'Built a desktop assistant with GPT‑4o, Whisper, and RAG for live guidance, answer drafting, and knowledge retrieval, packaged for frictionless installs.',
        results: ['70% increase in user engagement', '60% faster resume tailoring', 'Improved interview readiness scores'],
        technologies: ['GPT-4o', 'OpenAI Whisper', 'RAG', 'Electron/desktop', 'Tailwind CSS', 'Docker'],
        duration: '6 weeks'
      }
    ],
    trends: [
      'Personalized learning and adaptive education',
      'AI-powered tutoring and learning assistants',
      'Automated grading and assessment tools',
      'Learning analytics and predictive insights'
    ]
  }
};

// Helper functions
export const getIndustryById = (id) => {
  return industriesData[id] || null;
};

export const getAllIndustries = () => {
  return Object.values(industriesData);
};

export const getIndustryIds = () => {
  return Object.keys(industriesData);
};

// Industry categories for filtering
export const industryCategories = {
  technology: ['cybersecurity'],
  healthcare: ['healthcare'],
  finance: ['banking'],
  retail: ['retail'],
  logistics: ['transport'],
  education: ['education']
};

// Industry comparison data
export const industryComparison = {
  metrics: ['AI Adoption Rate', 'ROI Potential', 'Implementation Time', 'Compliance Complexity'],
  data: {
    healthcare: [85, 40, 12, 90],
    banking: [80, 35, 10, 95],
    retail: [75, 45, 8, 70],
    transport: [70, 50, 14, 75],
    cybersecurity: [90, 60, 6, 85],
    education: [65, 55, 10, 80]
  }
}; 